import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { LedgerRow } from './LedgerRow';
import type { Ledger, LedgerType } from '@/api/types';

interface SortableLedgerRowProps {
    ledger: Ledger;
    type: LedgerType;
    onConfirmUpdate: (ledgerId: number) => void;
    onRevertUpdate: (ledgerId: number) => void;
    isUpdating: boolean;
    isDragEnabled?: boolean;
    isLast?: boolean;
    userLatestConfirmationDate?: number | null;
}

/**
 * Wrapper around LedgerRow that enables drag-and-drop reordering.
 * Uses dnd-kit's useSortable hook for smooth drag interactions.
 * Drag handle only visible when isDragEnabled is true.
 */
export function SortableLedgerRow({
    ledger,
    type,
    onConfirmUpdate,
    onRevertUpdate,
    isUpdating,
    isDragEnabled = false,
    isLast = false,
    userLatestConfirmationDate,
}: SortableLedgerRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: ledger.id,
        disabled: !isDragEnabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    const isArchived = ledger.archivedAt !== null && ledger.archivedAt > 0;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative ${isDragging ? 'shadow-lg' : ''} ${isLast ? '' : 'border-b border-gray-300 dark:border-gray-700'} ${
                isArchived ? '' : 'bg-white dark:bg-secondary [@media(hover:hover)]:hover:bg-gray-50 [@media(hover:hover)]:dark:hover:bg-gray-700'
            }`}
        >
            {/* Drag Handle - Always visible on desktop, disabled state when not draggable */}
            <div
                {...(isDragEnabled ? { ...attributes, ...listeners } : {})}
                className={`hidden lg:flex absolute left-0 top-0 bottom-0 w-8 items-center justify-center z-10 transition-colors ${
                    isDragEnabled
                        ? 'cursor-grab active:cursor-grabbing'
                        : 'cursor-not-allowed'
                }`}
                title={isDragEnabled ? undefined : 'Reordering not available'}
            >
                <Bars3Icon className={`h-4 w-4 ${isDragEnabled ? 'text-gray-400' : 'text-gray-300 dark:text-gray-600'}`} />
            </div>

            {/* Adjust LedgerRow to have left padding for drag handle - always on desktop */}
            <div className="lg:pl-8">
                <LedgerRow
                    ledger={ledger}
                    type={type}
                    onConfirmUpdate={onConfirmUpdate}
                    onRevertUpdate={onRevertUpdate}
                    isUpdating={isUpdating}
                    userLatestConfirmationDate={userLatestConfirmationDate}
                />
            </div>
        </div>
    );
}
